import { type ReactNode, useEffect, useState } from 'react';
import { ScrollView, StyleSheet, Text, View, type StyleProp, type ViewStyle } from 'react-native';

import { Fonts, Palette } from '@/constants/theme';
import { getDocPreviewText, isTextPreviewable } from '@/lib/doc-preview';

/**
 * Drive-style "paper page" previews (Wave 56.1) for text-bearing
 * documents (docx, xlsx, pptx, txt, csv, eml, html, rtf…). The file's
 * ACTUAL text is rendered small onto a white page — at tile size it
 * reads as a genuine document thumbnail; the full variant is a
 * readable, scrollable page. Both work identically on web and native
 * because the input is extracted text, not a rasterized bitmap.
 */

export { isTextPreviewable };

export function DocPageThumb({
  uri,
  mime,
  name,
  style,
  fallback,
}: {
  uri: string;
  mime: string | null | undefined;
  name: string | null | undefined;
  style?: StyleProp<ViewStyle>;
  fallback: ReactNode;
}) {
  const [text, setText] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let alive = true;
    if (!isTextPreviewable(mime, name)) {
      setFailed(true);
      return;
    }
    getDocPreviewText(uri, mime, name).then(
      (t) => {
        if (!alive) return;
        if (t) setText(t);
        else setFailed(true);
      },
      () => alive && setFailed(true)
    );
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uri]);

  if (failed || !text) return <>{fallback}</>;
  return (
    <View style={[style, styles.page]} pointerEvents="none">
      <Text style={styles.thumbText}>{text.slice(0, 1200)}</Text>
    </View>
  );
}

/** Full-size readable page for the preview modal. */
export function DocPageFull({
  uri,
  mime,
  name,
  fallback = null,
}: {
  uri: string;
  mime: string | null | undefined;
  name: string | null | undefined;
  fallback?: ReactNode;
}) {
  const [text, setText] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let alive = true;
    if (!isTextPreviewable(mime, name)) {
      setFailed(true);
      return;
    }
    getDocPreviewText(uri, mime, name).then(
      (t) => {
        if (!alive) return;
        if (t) setText(t);
        else setFailed(true);
      },
      () => alive && setFailed(true)
    );
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uri]);

  if (failed) return <>{fallback}</>;
  return (
    <ScrollView style={styles.fullWrap} contentContainerStyle={styles.fullContent}>
      <View style={styles.fullPage}>
        <Text selectable style={styles.fullText}>
          {text ?? 'Reading the document…'}
        </Text>
      </View>
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  page: {
    backgroundColor: '#FFFFFF',
    padding: 10,
    overflow: 'hidden',
  },
  thumbText: {
    fontFamily: Fonts.sansMedium,
    fontSize: 6.5,
    lineHeight: 9,
    color: '#3A362E',
  },
  fullWrap: { flex: 1, backgroundColor: '#1a1714' },
  fullContent: { padding: 14 },
  fullPage: {
    backgroundColor: '#FFFFFF',
    borderRadius: 10,
    padding: 20,
    minHeight: 400,
  },
  fullText: {
    fontFamily: Fonts.sansMedium,
    fontSize: 13,
    lineHeight: 20,
    color: Palette.ink,
  },
});
